import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { isBriquezi, isBriqueziAnota, isBriqueziNet, BRAND_NAME } from "@/const";

export interface CompanyBranding {
  id?: number;
  legalName?: string | null;
  tradeName?: string | null;
  logoUrl?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  customDomain?: string | null;
  customFavicon?: string | null;
  themePreference?: string | null;
  hasLandingPage?: boolean | number | null;
}

export function getDefaultTenantColors(): { primary: string; secondary: string; brandName: string } {
  if (isBriqueziAnota) {
    return { primary: "#8b5cf6", secondary: "#2563eb", brandName: "Anota Briquezi" };
  }
  if (isBriqueziNet) {
    return { primary: "#2563eb", secondary: "#7c3aed", brandName: "Briquezi Soluções" };
  }
  if (isBriquezi) {
    return { primary: "#2563eb", secondary: "#7c3aed", brandName: "Briquezi Soluções" };
  }
  return { primary: "#ea580c", secondary: "#0284c7", brandName: "Só Facilitei" };
}

type Theme = "light" | "dark";

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme, userId?: number | null) => void;
  toggleTheme: (userId?: number | null) => void;
  resetThemeToDefault: () => void;
  switchable: boolean;
  branding: CompanyBranding | null;
  setCompanyBranding: (branding: CompanyBranding | null) => void;
  isBriqueziTheme: boolean;
  effectiveBrandName: string;
  effectiveLogoUrl: string | null;
  effectivePrimaryColor: string;
  effectiveSecondaryColor: string;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
  switchable?: boolean;
}

export const getTenantKey = (key: string, userId?: number | null): string => {
  if (typeof window === "undefined") return key;
  const host = window.location.hostname.replace(/:d+$/, "");
  const port = window.location.port ? `_${window.location.port}` : "";
  if (userId) {
    return `${key}_${host}${port}_user_${userId}`;
  }
  return `${key}_${host}${port}`;
};

const getInitialBranding = (): CompanyBranding | null => {
  if (typeof window === "undefined") return null;
  try {
    const cached = localStorage.getItem(getTenantKey("tenant_branding"));
    if (cached) return JSON.parse(cached);
  } catch (e) {}
  return null;
};

const getInitialTheme = (defaultTheme: Theme = "light"): Theme => {
  return defaultTheme;
};

function areBrandingsEqual(a: CompanyBranding | null, b: CompanyBranding | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.primaryColor === b.primaryColor &&
    a.secondaryColor === b.secondaryColor &&
    a.logoUrl === b.logoUrl &&
    a.themePreference === b.themePreference &&
    a.tradeName === b.tradeName &&
    a.customFavicon === b.customFavicon
  );
}

export function ThemeProvider({
  children,
  defaultTheme = "light",
  switchable = true,
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => getInitialTheme(defaultTheme));
  const [branding, setBrandingState] = useState<CompanyBranding | null>(getInitialBranding);

  const setTheme = useCallback((newTheme: Theme, userId?: number | null) => {
    setThemeState((current) => (current === newTheme ? current : newTheme));
    if (typeof window !== "undefined") {
      try {
        if (userId) {
          localStorage.setItem(getTenantKey("theme", userId), newTheme);
        }
        localStorage.removeItem(getTenantKey("theme"));
      } catch (e) {}
    }
  }, []);

  const toggleTheme = useCallback((userId?: number | null) => {
    setThemeState((current) => {
      const nextTheme = current === "dark" ? "light" : "dark";
      if (typeof window !== "undefined") {
        try {
          if (userId) {
            localStorage.setItem(getTenantKey("theme", userId), nextTheme);
          }
          localStorage.removeItem(getTenantKey("theme"));

          fetch("/api.php?route=auth.updateTheme", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              json: {
                themePreference: nextTheme,
              },
            }),
          }).catch(() => {});
        } catch (e) {}
      }
      return nextTheme;
    });
  }, []);

  const resetThemeToDefault = useCallback(() => {
    setThemeState("light");
    if (typeof document !== "undefined") {
      document.documentElement.classList.remove("dark");
    }
    if (typeof window !== "undefined") {
      try {
        localStorage.removeItem(getTenantKey("theme"));
      } catch (e) {}
    }
  }, []);

  const setBranding = useCallback((newBranding: CompanyBranding | null) => {
    setBrandingState((prev) => {
      if (areBrandingsEqual(prev, newBranding)) {
        return prev;
      }
      return newBranding;
    });
    if (typeof window !== "undefined" && newBranding) {
      try {
        localStorage.setItem(getTenantKey("tenant_branding"), JSON.stringify(newBranding));
      } catch (e) {}
    }
  }, []);

  const tenantDefaults = useMemo(() => getDefaultTenantColors(), []);
  const isDark = theme === "dark";
  const defaultDarkPrimary = "#ff6b00";

  const effectivePrimary = useMemo(() => {
    return branding?.primaryColor || (isDark ? defaultDarkPrimary : tenantDefaults.primary);
  }, [branding?.primaryColor, isDark, tenantDefaults.primary]);

  const effectiveSecondary = useMemo(() => {
    return branding?.secondaryColor || (isDark ? "#38bdf8" : tenantDefaults.secondary);
  }, [branding?.secondaryColor, isDark, tenantDefaults.secondary]);

  // Injeção de Tema e Personalização em Linhas, Bordas, Abas e Botões
  useEffect(() => {
    const root = document.documentElement;
    const isPanelRoute = typeof window !== "undefined" && (
      window.location.pathname.startsWith("/admin") ||
      window.location.pathname.startsWith("/portal")
    );

    // O modo dark é aplicado exclusivamente nas rotas do painel / dashboard
    if (theme === "dark" && isPanelRoute) {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }

    if (isBriquezi) {
      root.classList.add("theme-briquezi");

      // Atualiza Favicons
      const links = document.querySelectorAll("link[rel*='icon']");
      links.forEach((link) => {
        link.setAttribute("href", branding?.customFavicon || "/img-clientes/favicon_icone_briquezi.ico?v=1");
      });

      // Meta Theme Color
      const themeColor = document.querySelector("meta[name='theme-color']");
      if (themeColor) {
        themeColor.setAttribute("content", theme === "dark" ? "#0f1115" : effectivePrimary);
      }

      document.title = branding?.tradeName 
        ? `${branding.tradeName} emissor Fiscal` 
        : `Portal de Cliente ${tenantDefaults.brandName}`;
    } else {
      root.classList.remove("theme-briquezi");

      if (branding?.customFavicon) {
        const links = document.querySelectorAll("link[rel*='icon']");
        links.forEach((link) => {
          link.setAttribute("href", branding.customFavicon!);
        });
      }

      if (branding?.tradeName) {
        document.title = `${branding.tradeName} Área do Cliente`;
      }
    }

    // Injeção Dinâmica de Variáveis CSS Semânticas
    root.style.setProperty("--primary", effectivePrimary);
    root.style.setProperty("--sidebar-primary", effectivePrimary);
    root.style.setProperty("--ring", effectivePrimary);
    root.style.setProperty("--brand-orange", effectivePrimary);
    root.style.setProperty("--brand-purple", effectiveSecondary);
    root.style.setProperty("--color-chart-1", effectivePrimary);
    root.style.setProperty("--color-chart-2", effectiveSecondary);

    // Ajuste de opacidades e cores de destaque da barra lateral
    const accentAlpha = theme === "dark" ? "1f" : "14";
    root.style.setProperty("--sidebar-accent", `${effectivePrimary}${accentAlpha}`);
    root.style.setProperty("--sidebar-accent-foreground", effectivePrimary);
    root.style.setProperty("--accent", `${effectivePrimary}${accentAlpha}`);
    root.style.setProperty("--accent-foreground", effectivePrimary);

  }, [theme, branding?.customFavicon, branding?.tradeName, effectivePrimary, effectiveSecondary, tenantDefaults.brandName]);

  const effectiveBrandName = useMemo(() => {
    return branding?.tradeName || branding?.legalName || tenantDefaults.brandName;
  }, [branding?.tradeName, branding?.legalName, tenantDefaults.brandName]);
  const effectiveLogoUrl = useMemo(() => {
    return branding?.logoUrl || null;
  }, [branding?.logoUrl]);

  const contextValue = useMemo<ThemeContextType>(() => ({
    theme,
    setTheme,
    toggleTheme,
    resetThemeToDefault,
    switchable,
    branding,
    setCompanyBranding: setBranding,
    isBriqueziTheme: isBriquezi,
    effectiveBrandName,
    effectiveLogoUrl,
    effectivePrimaryColor: effectivePrimary,
    effectiveSecondaryColor: effectiveSecondary,
  }), [
    theme,
    setTheme,
    toggleTheme,
    resetThemeToDefault,
    switchable,
    branding,
    setBranding,
    effectiveBrandName,
    effectiveLogoUrl,
    effectivePrimary,
    effectiveSecondary,
  ]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
}

